import React, { useCallback, useState } from 'react';

import { Root } from '@atlaskit/navigation-system/layout/root';
import { usePanelContext } from '@atlassian/entry-point-panel/use-panel-context';
import { AsyncRovoAgentCreateModal } from '@atlassian/rovo-agent-create-modal/async';
import { defineMessage, useIntl } from 'react-intl';

import { useRovoWorkspace } from '@townsquare/collab-context';
import { GoalCapabilitiesProvider } from '@townsquare/goal-capabilities';
import { GriffinLayout } from '@townsquare/griffin-layout';
import { GriffinTopNavigation } from '@townsquare/griffin-navigation';
import { GlobalAppShortcutsProvider } from '@townsquare/griffin-navigation/global-navigation-context';
import type { RenderLegacyTopNavContent } from '@townsquare/griffin-navigation/types';
import { ErrorBoundary } from '@townsquare/monitoring/error-boundary';
import {
	TownsquareGlobalRoot,
	useTownsquareLegacyTopNavRenderer,
} from '@townsquare/nav-platform-integration/global-root';
import { AppHomeWrapper } from '@townsquare/navigation/app-home-wrapper';
import { IntroPopup } from '@townsquare/onboarding/intro-popup';
import RovoAppificationChangeboarding from '@townsquare/post-office/message-components/rovo-appification-modal';
import { TownsquareGlobalPlacement } from '@townsquare/post-office/placements';
import { ProjectCapabilitiesProvider } from '@townsquare/project-capabilities';
import { useRovoAppification } from '@townsquare/rovo/appification';
import { SIDEBAR_PANEL } from '@townsquare/sidebar-panel/panels';
import { usePreloadedEntryPointPanel } from '@townsquare/sidebar-panel-utils/hooks/use-preloaded-entry-point-panel';
import { SiteBannerProvider } from '@townsquare/site-banner';
import { FullScreenSpaLoader } from '@townsquare/spa-loader';
import { expVal } from '@townsquare/stat-sig';
import { NAVX_UC_ML_POWERED_RELEVANCY_ENGINE } from '@townsquare/stat-sig/experiments';
import { fg } from '@townsquare/stat-sig/gate';
import { useNoWorkspaceExperience } from '@townsquare/workspace-store';

import { ProductHome } from '../navigation/ProductHome';
import { useUniversalCreateItems } from '../navigation/use-universal-create-items';

import { ConditionalSideNav, useDisableRovoSearchSideNav } from './ConditionalSideNav';
import { useHomeNavigationCustomizationStore } from './home-navigation-customization-store';
import { HOME_TOP_NAV_START_SLOTS } from './home-top-nav-slots';
import { useIsOnRovoSearch } from './hooks';

const HOME_APP = 'home';

const homeAppLabelMessage = defineMessage({
	id: 'townsquare.home.app.label',
	defaultMessage: 'Home',
	description: 'Label for the app when shown in the global side navigation.',
});

type Props = {
	/**
	 * Prop to disable customization of the Home sidebar, for testing purposes.
	 */
	sidebarCustomizationDisabled?: boolean;
};

export function GriffinRoute(props: Props): React.JSX.Element {
	const { rovoAppificationState } = useRovoAppification();

	if (rovoAppificationState === 'loading') {
		return <FullScreenSpaLoader />;
	}

	return fg('navx-global-top-nav-home') ? (
		<GriffinGlobalRoute {...props} />
	) : (
		<GriffinLegacyRoute {...props} />
	);
}

export function GriffinLegacyRoute(props: Props): React.JSX.Element {
	return (
		<Root isSideNavShortcutEnabled>
			<SiteBannerProvider>
				<GlobalAppShortcutsProvider>
					<GriffinRouteComponent {...props} />
				</GlobalAppShortcutsProvider>
			</SiteBannerProvider>
		</Root>
	);
}

export function GriffinGlobalRoute({ sidebarCustomizationDisabled }: Props): React.JSX.Element {
	const { formatMessage } = useIntl();
	const customizationStore = useHomeNavigationCustomizationStore(sidebarCustomizationDisabled);
	const noWorkspaceExperience = useNoWorkspaceExperience();
	const preloadedPanel = usePreloadedEntryPointPanel();
	const { Panel: SidebarPanel, Provider: SidebarPanelContext } = usePanelContext(
		SIDEBAR_PANEL,
		preloadedPanel,
	);

	return (
		<SidebarPanelContext>
			<GlobalAppShortcutsProvider>
				<TownsquareGlobalRoot
					app={HOME_APP}
					appLabel={formatMessage(homeAppLabelMessage)}
					customizationStore={customizationStore}
					TopNav={HomeGlobalTopNav}
					sideNav={<ConditionalSideNav customizationDisabled={sidebarCustomizationDisabled} />}
				>
					<SiteBannerProvider>
						<TownsquareGlobalPlacement />
						<RovoAppificationChangeboarding />
						<GriffinLayout PlatformSidebarPanel={SidebarPanel} />
						{!fg('townsquare_deprecate_hisi_onboarding') && noWorkspaceExperience && <IntroPopup />}
					</SiteBannerProvider>
				</TownsquareGlobalRoot>
			</GlobalAppShortcutsProvider>
		</SidebarPanelContext>
	);
}

/**
 * Top navigation for the gated `navx-global-top-nav-home` route. Renders the Home top nav through
 * the shared Townsquare GlobalRoot so the legacy content renderer is supplied.
 */
function HomeGlobalTopNav() {
	const renderLegacyTopNavContent = useTownsquareLegacyTopNavRenderer(HOME_TOP_NAV_START_SLOTS);
	const disableSideNavRovoSearch = useDisableRovoSearchSideNav();
	const showRovoSearchNavigationWhileLoading =
		useIsOnRovoSearch() && fg('rovo-search-team-eu-26-search-app');
	const loadingTopNav = showRovoSearchNavigationWhileLoading ? (
		<GriffinTopSectionLoading
			hideSideNav={disableSideNavRovoSearch}
			renderLegacyTopNavContent={renderLegacyTopNavContent}
		/>
	) : null;

	return (
		<GoalCapabilitiesProvider fallback={loadingTopNav}>
			<ProjectCapabilitiesProvider fallback={loadingTopNav}>
				<GriffinTopSection
					hideSideNav={disableSideNavRovoSearch}
					renderLegacyTopNavContent={renderLegacyTopNavContent}
				/>
			</ProjectCapabilitiesProvider>
		</GoalCapabilitiesProvider>
	);
}

type GriffinTopSectionProps = {
	hideSideNav?: boolean;
	/**
	 * Only supplied by the gated GlobalRoot route.
	 */
	renderLegacyTopNavContent?: RenderLegacyTopNavContent;
};

function GriffinRouteComponent({ sidebarCustomizationDisabled }: Props) {
	const noWorkspaceExperience = useNoWorkspaceExperience();
	const preloadedPanel = usePreloadedEntryPointPanel();
	const { Panel: SidebarPanel, Provider: SidebarPanelContext } = usePanelContext(
		SIDEBAR_PANEL,
		preloadedPanel,
	);
	const disableSideNavRovoSearch = useDisableRovoSearchSideNav();
	const showRovoSearchNavigationWhileLoading =
		useIsOnRovoSearch() && fg('rovo-search-team-eu-26-search-app');
	const loadingTopNav = showRovoSearchNavigationWhileLoading ? (
		<GriffinTopSectionLoading hideSideNav={disableSideNavRovoSearch} />
	) : null;

	return (
		<>
			<TownsquareGlobalPlacement />
			<RovoAppificationChangeboarding />
			<SidebarPanelContext>
				<GoalCapabilitiesProvider fallback={loadingTopNav}>
					<ProjectCapabilitiesProvider fallback={loadingTopNav}>
						<GriffinTopSection hideSideNav={disableSideNavRovoSearch} />
					</ProjectCapabilitiesProvider>
				</GoalCapabilitiesProvider>
				<ConditionalSideNav customizationDisabled={sidebarCustomizationDisabled} />
				<GriffinLayout PlatformSidebarPanel={SidebarPanel} />
			</SidebarPanelContext>
			{!fg('townsquare_deprecate_hisi_onboarding') && noWorkspaceExperience && <IntroPopup />}
		</>
	);
}

const GriffinTopSection = ({ hideSideNav, renderLegacyTopNavContent }: GriffinTopSectionProps) => {
	const rovoWorkspaceCloudId = useRovoWorkspace();
	const [isCreateAgentModalOpen, setIsCreateAgentModalOpen] = useState(false);
	const isDraftAgentCreationEnabled = fg('sltns-1529-draft-agent-non-consequential');
	const openCreateAgentModal = useCallback(() => setIsCreateAgentModalOpen(true), []);
	const closeCreateAgentModal = useCallback(() => setIsCreateAgentModalOpen(false), []);
	const {
		items: universalCreateItems,
		compact,
		isItemLoading,
		foldable,
	} = useUniversalCreateItems({
		onCreateAgent:
			isDraftAgentCreationEnabled && rovoWorkspaceCloudId ? openCreateAgentModal : undefined,
	});

	return (
		<>
			<GriffinTopNavigation
				renderProductHome={renderProductHome}
				universalCreateItems={universalCreateItems}
				compact={compact}
				enableCrossAppCreation={
					expVal(NAVX_UC_ML_POWERED_RELEVANCY_ENGINE, 'isEnabled', false)
						? 'personalizeWithGrowthRecommendations'
						: 'personalize'
				}
				hideSideNavToggleButton={hideSideNav}
				isItemLoading={isItemLoading}
				foldable={foldable}
				UNSAFE__renderLegacyTopNavContent={renderLegacyTopNavContent}
			/>
			{isDraftAgentCreationEnabled && isCreateAgentModalOpen && rovoWorkspaceCloudId ? (
				<ErrorBoundary onError={closeCreateAgentModal} fallback={null}>
					<AsyncRovoAgentCreateModal
						cloudId={rovoWorkspaceCloudId}
						product="atlassian-studio"
						source="townsquare-universal-create-button"
						onModalClose={closeCreateAgentModal}
					/>
				</ErrorBoundary>
			) : null}
		</>
	);
};

const renderProductHome = () => (
	<AppHomeWrapper>
		<ProductHome />
	</AppHomeWrapper>
);

const GriffinTopSectionLoading = ({
	hideSideNav,
	renderLegacyTopNavContent,
}: GriffinTopSectionProps) => (
	<GriffinTopNavigation
		renderProductHome={renderProductHome}
		universalCreateItems={[]}
		hideSideNavToggleButton={hideSideNav}
		isItemLoading
		UNSAFE__renderLegacyTopNavContent={renderLegacyTopNavContent}
	/>
);
